import { portalText } from "../../i18n/portal.js";
/**
 * The per-minute dial limit, as a member meets it (plan-008 ST-RV2; P6, review C2).
 *
 * "Ring a number now" on the Outbound tab and "The agent calls me" on a trunk's test call share one
 * limit — 10 a minute per account, 3 per API key. Over it the API answers `429 rate_limited`, and its
 * `Retry-After` (carried by `RetryAfterApiError`, `api/retry-after.ts`) says when the next one may
 * start. The dialog then says so next to its button, counts the seconds down, holds the button for
 * exactly that long, and gives it back when the wait is over. A `429` that named no wait is not
 * counted down here: the dialog shows the API's own sentence instead, and nothing is held.
 */
import { ApiError } from "@wetalk/api-client";
import { useEffect, useState } from "react";

import { retryAfterSecondOf } from "../../api/retry-after.js";

/** How often a running wait re-reads the clock. Shorter than a second, so the count never skips. */
const DIAL_WAIT_TICK_MS = 250;

/** The sentence beside the button while the wait runs. */
export function dialRateLimitedSentence(second: number): string {
  return portalText("operations", "too_many_calls_started_in_the_last_minute_try_again_in_val_01bee0a8", { v0: String(String(second)), v1: String(second === 1 ? portalText("operations", "second_352f7829") : portalText("operations", "seconds_7febce03")) });
}

/** The seconds to wait, when this refusal is the per-minute dial limit and named them; else `null`. */
export function dialWaitOf(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.code !== "rate_limited") return null;
  const second = retryAfterSecondOf(error);
  return second === null || second <= 0 ? null : second;
}

export interface DialWait {
  /** Whole seconds left, never below 1 while it runs; `null` when there is nothing to wait for. */
  readonly remainingSecond: number | null;
  readonly start: (second: number) => void;
}

export function useDialWait(): DialWait {
  const [untilMs, setUntilMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (untilMs === null) return undefined;
    const timer = setInterval(() => {
      const at = Date.now();
      setNowMs(at);
      if (at >= untilMs) setUntilMs(null);
    }, DIAL_WAIT_TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [untilMs]);

  return {
    remainingSecond: untilMs === null ? null : Math.max(1, Math.ceil((untilMs - nowMs) / 1000)),
    start: (second) => {
      const at = Date.now();
      setNowMs(at);
      setUntilMs(at + second * 1000);
    },
  };
}
